import { Modal, Button, Icon, Form } from 'semantic-ui-react';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useToasts } from 'common/toast';
import FileViewer from './FileViewer';
import KnowledgeBaseContentShareModal from 'areas/knowledge-base/components/KnowledgeBaseContentShareModal';
import { useGetFileUploadQuery, useVoteFileUploadMutation } from '../api/repositoryApi';
import { getMemberId } from 'common/selectors/auth';

type FileFilterModalProps = {
  fileId: string;
  isOpen: boolean;
  setModalOpen: (open: boolean) => void;
  title?: string;
};

type VoteType = 1 | -1;

export const FileViewModal = (props: FileFilterModalProps) => {
  const [isShareModalOpen, setShareModalOpen] = useState(false);
  const [isFeedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [feedbackNotes, setFeedbackNotes] = useState('');
  const [isVotePending, setIsVotePending] = useState(false);
  const [voteFileUpload, { isLoading: isVoteLoading }] = useVoteFileUploadMutation();
  const { addToast } = useToasts();
  const currentEmployeeId = useSelector(getMemberId);
  const fileName = props.title ?? 'Resource Viewer';
  const { data: fileData, isFetching: isFileFetching, isLoading: isFileLoading } =
    useGetFileUploadQuery(props.fileId, { skip: !props.isOpen });
  const isFileLoadedForCurrentFile = Boolean(fileData && String(fileData.id) === props.fileId);

  const canVote = !isVotePending && !isFileFetching && !isFileLoading && isFileLoadedForCurrentFile;

  const handleVoteButtonClick = async (vote: VoteType) => {
    if (!canVote) {
      return;
    }

    if (vote === 1) {
      setIsVotePending(true);
      try {
        await voteFileUpload({
          id: props.fileId,
          vote,
          employee_id: currentEmployeeId || undefined,
        }).unwrap();
        addToast('Thanks for your feedback!', { appearance: 'success' });
      } catch (err) {
        // swallow errors; API handles validation.
      } finally {
        setIsVotePending(false);
      }
      return;
    }

    setFeedbackNotes(fileData?.your_feedback_notes ?? '');
    setFeedbackModalOpen(true);
  };

  const handleCloseFeedbackModal = () => {
    setFeedbackNotes('');
    setFeedbackModalOpen(false);
  };

  const handleSubmitFeedback = async () => {
    setIsVotePending(true);
    try {
      await voteFileUpload({
        id: props.fileId,
        vote: -1,
        feedback_notes: feedbackNotes || undefined,
        employee_id: currentEmployeeId || undefined,
      }).unwrap();
      addToast('Thanks for your feedback!', { appearance: 'success' });
    } catch (err) {
      // swallow errors; API handles validation.
    } finally {
      setIsVotePending(false);
      handleCloseFeedbackModal();
    }
  };

  return (
    <>
      <Modal
        open={props.isOpen}
        onClose={() => props.setModalOpen(false)}
        size="fullscreen"
        closeIcon
        dimmer={{ style: { zIndex: 1002 } }}
        style={{ zIndex: 1003, maxHeight: 'calc(100vh - 80px)' }}
      >
        <Modal.Header>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <span>{fileName}</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginRight: '2rem' }}>
              <Button
                icon
                title="Helpful"
                aria-label="Helpful"
                color={!canVote ? 'grey' : fileData?.your_vote === 1 ? 'green' : 'grey'}
                size="small"
                onClick={() => handleVoteButtonClick(1)}
                disabled={!canVote || isVoteLoading}
              >
                <Icon name="thumbs up" />
              </Button>
              <Button
                icon
                title="Not helpful"
                aria-label="Not helpful"
                color={!canVote ? 'grey' : fileData?.your_vote === -1 ? 'red' : 'grey'}
                size="small"
                onClick={() => handleVoteButtonClick(-1)}
                disabled={!canVote || isVoteLoading}
              >
                <Icon name="thumbs down" />
              </Button>
              <Button
                icon
                labelPosition="left"
                color="blue"
                size="small"
                onClick={() => setShareModalOpen(true)}
              >
                <Icon name="share" />
                Share resource
              </Button>
            </div>
          </div>
        </Modal.Header>
        <Modal.Content style={{ height: 'calc(100vh - 120px)', padding: 0, overflow: 'hidden', minHeight: 0 }}>
          <div
            style={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              padding: '4px 0 0 0',
              width: '100%',
              boxSizing: 'border-box',
              minHeight: 0,
              minWidth: 0,
              overflow: 'hidden',
            }}
          >
            <FileViewer
              fileId={props.fileId}
              isFullScreen={true}
              style={{ width: '100%', height: '100%', minHeight: 0, minWidth: 0 }}
            />
          </div>
        </Modal.Content>
      </Modal>

      <KnowledgeBaseContentShareModal
        fileId={props.fileId}
        fileName={fileName}
        isOpen={isShareModalOpen}
        setModalOpen={setShareModalOpen}
      />

      <Modal
        open={isFeedbackModalOpen}
        onClose={handleCloseFeedbackModal}
        size="small"
        closeIcon
        dimmer={{ style: { zIndex: 1102 } }}
        style={{ zIndex: 1103, top: '50%', transform: 'translateY(-50%)' }}
      >
        <Modal.Header>We&apos;re sorry this content wasn&apos;t helpful.</Modal.Header>
        <Modal.Content>
          <div style={{ marginBottom: 16 }}>            
            We&apos;d be grateful for more detail so we can improve future recommendations.
          </div>
          <Form>
            <Form.TextArea
              placeholder="Optional feedback notes..."
              value={feedbackNotes}
              onChange={(_, data) => setFeedbackNotes(data.value as string)}
              style={{ minHeight: 150 }}
            />
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={handleCloseFeedbackModal} disabled={isVoteLoading}>
            Cancel
          </Button>
          <Button
            primary
            loading={isVoteLoading}
            onClick={handleSubmitFeedback}
          >
            Submit
          </Button>
        </Modal.Actions>
      </Modal>
    </>
  );
};
